import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import BarChartIcon from "@mui/icons-material/BarChart";
import AreaChartIcon from "@mui/icons-material/Landscape";
import PieChartIcon from "@mui/icons-material/PieChart";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Bar, Pie } from "react-chartjs-2";

import useGetPatientHistogramData from "../../hooks/useGetPatientHistogramData";
import Loader from "../../components/Loader";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const CHART_COLORS = [
  "#EE295D",
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#EC4899",
  "#06B6D4",
  "#84CC16",
];

const CHART_TYPES = [
  { value: "bar", label: "Bar", Icon: BarChartIcon },
  { value: "line", label: "Line", Icon: ShowChartIcon },
  { value: "area", label: "Area", Icon: AreaChartIcon },
  { value: "pie", label: "Pie", Icon: PieChartIcon },
];

const buildDataset = (question, color, chartType) => {
  const base = {
    label: question.title,
    borderColor: color,
    backgroundColor: `${color}33`,
    pointRadius: 5,
    pointHoverRadius: 7,
    spanGaps: true,
  };

  if (chartType === "pie") {
    return {
      label: question.title,
      data: question.data_points.map((dp) => dp.value),
      backgroundColor: CHART_COLORS.map((c) => `${c}CC`),
      borderColor: CHART_COLORS,
      borderWidth: 1,
    };
  }

  const data = question.data_points.map((dp) => dp.value);

  if (chartType === "area") return { ...base, data, fill: true, tension: 0.3 };
  if (chartType === "bar")
    return { ...base, data, borderWidth: 1, borderRadius: 6, backgroundColor: color };

  return { ...base, data, tension: 0.3 };
};

const formatChoiceValue = (question, value) => {
  if (!question.choices || value === null || value === undefined) return value;
  const label = question.choices[value];
  return label !== undefined ? label : value;
};

const buildOptions = (question, chartType) => {
  const values = question.data_points
    .map((dp) => dp.value)
    .filter((v) => v !== null && v !== undefined);

  const isPie = chartType === "pie";
  const isChoiceBased = Boolean(question.choices);

  let yScale = { beginAtZero: false, ticks: { precision: 1, font: { size: 11 } } };
  if (isChoiceBased) {
    const choiceValues = Object.keys(question.choices).map(Number);
    yScale = {
      min: Math.min(...choiceValues),
      max: Math.max(...choiceValues),
      ticks: { stepSize: 1, font: { size: 11 } },
    };
  } else if (values.length > 0) {
    yScale.min = Math.floor((Math.min(...values) - 1) / 10) * 10;
  }

  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: isPie,
        position: "bottom",
        labels: { boxWidth: 12, font: { size: 11 } },
      },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0.75)",
        padding: 10,
        callbacks: {
          title: (items) => items[0]?.label ?? "",
          label: (ctx) => {
            const y = isPie ? ctx.parsed : ctx.parsed.y;
            if (y === null || y === undefined) return "No data";
            return `${question.title}: ${formatChoiceValue(question, y)}`;
          },
        },
      },
    },
    ...(isPie
      ? {}
      : {
          scales: {
            x: {
              ticks: {
                maxRotation: 0,
                minRotation: 0,
                autoSkip: false,
                font: { size: 10 },
              },
              grid: { display: false },
            },
            y: {
              ...yScale,
              grid: { color: "rgba(0,0,0,0.06)" },
            },
          },
        }),
  };
};

const NoDataPlaceholder = ({ title }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 5,
      color: "text.disabled",
    }}
  >
    <BarChartIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
    <Typography variant="body2">
      No data recorded yet for <strong>{title}</strong>
    </Typography>
  </Box>
);

const QuestionChart = ({ question, index }) => {
  const [chartType, setChartType] = useState("bar");

  const color = CHART_COLORS[index % CHART_COLORS.length];
  const labels = question.data_points.map((dp) => dp.visit_name);
  const hasData = question.data_points.some((dp) => dp.value !== null);

  const chartData = {
    labels,
    datasets: [buildDataset(question, color, chartType)],
  };

  const options = buildOptions(question, chartType);
  const ChartComponent = chartType === "bar" ? Bar : chartType === "pie" ? Pie : Line;

  return (
    <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden", mb: 2 }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          backgroundColor: "grey.50",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          lineHeight={1.2}
          sx={{ flex: 1, minWidth: 0 }}
          noWrap
        >
          {question.title}
        </Typography>

        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={(_, val) => val && setChartType(val)}
          size="small"
          sx={{ backgroundColor: "white", borderRadius: 2, flexShrink: 0 }}
        >
          {CHART_TYPES.map(({ value, label, Icon }) => (
            <ToggleButton
              key={value}
              value={value}
              title={label}
              sx={{
                px: 1,
                "&.Mui-selected": {
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": { backgroundColor: "primary.dark" },
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }} />
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ px: 1.5, py: 2, overflowX: chartType === "pie" ? "visible" : "auto" }}>
        {hasData ? (
          <Box
            sx={{
              height: 300,
              minWidth:
                chartType === "pie" ? "100%" : `max(100%, ${question.data_points.length * 110}px)`,
            }}
          >
            <ChartComponent
              data={chartData}
              options={options}
              style={{ width: "100%", height: "100%" }}
            />
          </Box>
        ) : (
          <NoDataPlaceholder title={question.title} />
        )}
      </Box>
    </Paper>
  );
};

const PatientHistograms = () => {
  const { _id_: patientId } = useParams();
  const { trialFilter, setActive, setBreadcrumbTitle } = useOutletContext();

  setActive("histograms");
  setBreadcrumbTitle("Histograms");

  const [selectedFormIndex, setSelectedFormIndex] = useState(0);

  useEffect(() => {
    setSelectedFormIndex(0);
  }, [trialFilter]);

  const { data: forms, isLoading } = useGetPatientHistogramData({
    trialId: trialFilter,
    patientId,
    enabled: Boolean(trialFilter) && Boolean(patientId),
  });

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Histograms</Typography>
      </Stack>

      {/* Form selector */}
      <Stack direction="row" gap={2} mb={3} flexWrap="wrap">
        {!isLoading && forms && forms.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Form</InputLabel>
            <Select
              label="Form"
              value={selectedFormIndex}
              onChange={(e) => setSelectedFormIndex(e.target.value)}
            >
              {forms.map((form, idx) => (
                <MenuItem key={`${form.form_type}_${form.form_id}`} value={idx}>
                  {form.form_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>

      {isLoading && <Loader />}

      {!isLoading && (!forms || forms.length === 0) && (
        <Paper elevation={1} sx={{ borderRadius: 3, p: 5, textAlign: "center" }}>
          <BarChartIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            No charts configured
          </Typography>
          <Typography variant="body2" color="text.disabled">
            No histogram data has been configured for this trial yet.
          </Typography>
        </Paper>
      )}

      {!isLoading && forms && forms.length > 0 && (
        <>
          {/* Charts */}
          <Box>
            {forms[selectedFormIndex].questions.length === 0 ? (
              <Paper elevation={1} sx={{ borderRadius: 3, p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No questions configured for this form.
                </Typography>
              </Paper>
            ) : (
              forms[selectedFormIndex].questions.map((question, idx) => (
                <QuestionChart key={question.name} question={question} index={idx} />
              ))
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default PatientHistograms;
